import { useLocation } from "react-router-dom";
import * as amplitude from "@amplitude/analytics-browser";

import useNavigateWithParams from "libs/design-system/src/hooks/useNavigateWithParams";

import { SHARED_EVENTS, SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import type { ISubItem } from "@app/dashboard/types/innerLayout/nav-item";
import type { TInnerRoute, TNavbarSpecialActions } from "@app/dashboard/types/innerLayout/nav-links";
import { useNavigationStore } from "@app/dashboard/store/navigation";
import { fireEventBeforeNavigation } from "@app/dashboard/utils/tracking/fireEventBeforeNavigation";
import { isNavItemHidden } from "@app/dashboard/utils/navItemsSupportedRoutes";
import { getCurrentPath } from "@app/dashboard/utils/getCurrentPath";
import styles from "../nav-item.module.scss";

const SubItem = (
  {
    subItems,
    index,
    navItemLabel,
    navItemId,
    closeDropdown,
    onSubItemClick,
    collapsed
  }
    : ISubItem
) => {
  const location = useLocation();

  const { setActiveLink, specialAction: supportSpecialActions } = useNavigationStore();
  const navigateWithParams = useNavigateWithParams();

  const item = subItems[index];

  const handleSubItemClick = async (
    label: string,
    route: string,
    selectable?: boolean,
    specialAction?: TNavbarSpecialActions,
    id?: string
  ) => {
    

    if (specialAction) {
      await fireEventBeforeNavigation(
        () => amplitude.track(SHARED_EVENTS.NAVIGATION_ITEM_CLICKED, {
          [SHARED_EVENTS_PARAMS.NAV_ITEM]: id || label,
          [SHARED_EVENTS_PARAMS.FROM_PAGE]: getCurrentPath(),
          [SHARED_EVENTS_PARAMS.IS_COLLAPSED_NAV]: !!collapsed,
        })
      );

      supportSpecialActions?.(specialAction);

    } else {
      selectable && setActiveLink({ nav: navItemLabel, sub: label });
      closeDropdown?.();
    };

    if (route) {

      amplitude.track(SHARED_EVENTS.NAVIGATION_ITEM_CLICKED, {
        ...((id || label) ? { [SHARED_EVENTS_PARAMS.NAV_ITEM]: id || label } : {}),
        [SHARED_EVENTS_PARAMS.NAV_SECTION]: navItemId || navItemLabel,
        [SHARED_EVENTS_PARAMS.FROM_PAGE]: getCurrentPath(),
        [SHARED_EVENTS_PARAMS.TO_PAGE]: route,
        [SHARED_EVENTS_PARAMS.IS_COLLAPSED_NAV]: !!collapsed,
      });

      if (item.isExternalRoute) {
        window?.open(item.route, '_blank')?.focus();
      } else {
        navigateWithParams(route);
      };

      onSubItemClick?.();
    };
  };

  const SubItemBase = <div
    className={`${styles["sub-item"]} ${index === subItems.length - 1 ? styles["sub-item--last"] : ""}`}
    id={item.tourElementId}
    data-sub-item-active={item.active}
    key={`sub-item-${index}`}
    tabIndex={0}
    onClick={() => handleSubItemClick(item.label, item.route, item.selectable, item.specialAction, item.id)}
  >
    {item.icon}
    {item.label}
  </div>;

  const isSubItemHidden = isNavItemHidden(location.pathname as TInnerRoute, item.hiddenRoutes || []);

  return (
    !isSubItemHidden && (
      item.route ?
        <a href={
          item.isExternalRoute ?
            item.route
            :
            `${window.location.origin}${item.route}`
        }
          onClick={(e) => e.preventDefault()}
        >
          {SubItemBase}
        </a>
        :
        SubItemBase
    )
  )
}

export default SubItem;